import React, { FC, useState, useEffect, useRef } from 'react';
import { TimePickerProps, TimeValue } from './types';
import './TimePicker.scss';

const TimePicker: FC<TimePickerProps> = ({
    value,
    onChange,
    disabled = false,
    minuteStep = 15,
    placeholder = 'Select time',
    className = '',
    autoSelectCurrentTime = true,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    // Initialize with current time rounded up to next minute step
    const getCurrentTime = (): TimeValue => {
        const now = new Date();
        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();

        // Round up to next minute step
        const roundedMinutes = Math.ceil(currentMinutes / minuteStep) * minuteStep;

        let finalHours = currentHours;
        let finalMinutes = roundedMinutes;

        // Handle minute overflow (e.g., if current is 12:50 and step is 15, next should be 1:00)
        if (finalMinutes >= 60) {
            finalMinutes = 0;
            finalHours = (finalHours + 1) % 24;
        }

        let hour12 = finalHours;
        if (finalHours === 0) {
            hour12 = 12;
        } else if (finalHours > 12) {
            hour12 = finalHours - 12;
        }

        const period = (finalHours >= 12 ? 'PM' : 'AM') as 'AM' | 'PM';
        return { hour: hour12, minute: finalMinutes, period };
    };

    const [timeValue, setTimeValue] = useState<TimeValue>(getCurrentTime());
    const containerRef = useRef<HTMLDivElement>(null);

    // Convert TimeValue to HH:mm:ss format
    const formatTimeValue = (time: TimeValue): string => {
        let hour24 = time.hour;
        if (time.period === 'PM' && time.hour !== 12) {
            hour24 = time.hour + 12;
        } else if (time.period === 'AM' && time.hour === 12) {
            hour24 = 0;
        }
        return `${String(hour24).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}:00`;
    };

    // Initialize with current time on mount if no value provided
    useEffect(() => {
        if (!autoSelectCurrentTime) {
            return;
        }
        if (!value) {
            const currentTime = getCurrentTime();
            setTimeValue(currentTime);
            onChange(formatTimeValue(currentTime));
        }
    }, [minuteStep, autoSelectCurrentTime]); // Run when minuteStep changes too

    // Parse value string (HH:mm:ss format) to TimeValue when value changes
    useEffect(() => {
        if (value) {
            const [hours, minutes] = value.split(':').map(Number);
            if (hours !== undefined) {
                let hour12 = hours;
                if (hours === 0) {
                    hour12 = 12;
                } else if (hours > 12) {
                    hour12 = hours - 12;
                }
                const period = hours >= 12 ? 'PM' : 'AM';
                setTimeValue({
                    hour: hour12,
                    minute: minutes || 0,
                    period,
                });
            }
        }
    }, [value]);

    // Generate hours (1-12)
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);

    // Generate minutes based on step
    const minutes = Array.from({ length: 60 / minuteStep }, (_, i) => i * minuteStep);

    const handleHourChange = (hour: number) => {
        const newTime = { ...timeValue, hour };
        setTimeValue(newTime);
        onChange(formatTimeValue(newTime));
    };

    const handleMinuteChange = (minute: number) => {
        const newTime = { ...timeValue, minute };
        setTimeValue(newTime);
        onChange(formatTimeValue(newTime));
    };

    const handlePeriodToggle = () => {
        const newPeriod = timeValue.period === 'AM' ? 'PM' : 'AM' as 'AM' | 'PM';
        const newTime = { ...timeValue, period: newPeriod };
        setTimeValue(newTime);
        onChange(formatTimeValue(newTime));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const displayValue = value
        ? `${timeValue.hour}:${String(timeValue.minute).padStart(2, '0')}`
        : '';

    return (
        <div className={`custom-time-picker ${className} ${disabled ? 'disabled' : ''}`} ref={containerRef}>
            <div
                className={`time-picker-input ${isOpen ? 'open' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}>
                <span className={displayValue ? 'time-value' : 'time-placeholder'}>
                    {displayValue || placeholder}
                </span>
            </div>

            {isOpen && (
                <div className="time-picker-dropdown">
                    <div className="time-picker-content">
                        <div className="time-column">
                            <div className="column-header">Hour</div>
                            <div className="options-list">
                                {hours.map((hour) => (
                                    <div
                                        key={hour}
                                        className={`time-option ${timeValue.hour === hour ? 'selected' : ''}`}
                                        onClick={() => handleHourChange(hour)}>
                                        {hour}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="time-separator">:</div>

                        <div className="time-column">
                            <div className="column-header">Minute</div>
                            <div className="options-list">
                                {minutes.map((minute) => (
                                    <div
                                        key={minute}
                                        className={`time-option ${timeValue.minute === minute ? 'selected' : ''}`}
                                        onClick={() => handleMinuteChange(minute)}>
                                        {String(minute).padStart(2, '0')}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="period-toggle">
                        <button
                            type="button"
                            className={`period-button ${timeValue.period === 'AM' ? 'active' : ''}`}
                            onClick={handlePeriodToggle}>
                            AM
                        </button>
                        <button
                            type="button"
                            className={`period-button ${timeValue.period === 'PM' ? 'active' : ''}`}
                            onClick={handlePeriodToggle}>
                            PM
                        </button>
                    </div>
                </div>
            )}

            <div className="am-pm-display">{timeValue.period}</div>
        </div>
    );
};

export default TimePicker;
